/* Desktop menu lifecycle survives Shopify Theme Editor section replacement. */
(() => {
  if (customElements.get('lyma-mega-menu')) return;
  const editorMenus = new Map();
  customElements.define('lyma-mega-menu', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected && !this.cleanup) this.initialize(); });
    }
    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
    initialize() {


    const nav = this;
    const desktopQuery = window.matchMedia('(min-width: 990px)');
    const overlay = nav.nextElementSibling;
    const header = nav.closest('.section-header');
    const menus = [...nav.querySelectorAll('.lyma-mega')];
    const sectionKey = header.id;
    const restore = window.Shopify?.designMode && editorMenus.get(sectionKey);
    const controller = new AbortController();
    const on = (target, type, listener, options = {}) => target.addEventListener(type, listener, { ...options, signal: controller.signal });
    const states = new Map(menus.map(menu => [menu, { timer: 0, version: 0 }]));
    let metricsFrame;
    const syncMetrics = () => {
      if (!desktopQuery.matches) return;
      const bottom = Math.max(0, header.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--lyma-visible-header-height', `${bottom}px`);
      const innerHeader = nav.closest('header');
      const borderTop = parseFloat(getComputedStyle(innerHeader).borderTopWidth) || 0;
      nav.style.setProperty('--lyma-menu-top', `${bottom - innerHeader.getBoundingClientRect().top - borderTop}px`);
    };
    const syncOverlay = () => {
      const mounted = desktopQuery.matches && menus.some(menu => menu.open);
      overlay.classList.toggle('is-mounted', mounted);
      overlay.classList.toggle('is-open', menus.some(menu => menu.querySelector('.is-open')));
      document.documentElement.classList.toggle('lyma-desktop-menu-open', mounted);
      if (window.Shopify?.designMode) {
        const active = menus.find(menu => menu.open);
        if (active) editorMenus.set(sectionKey, { id: active.id, scrollTop: active.querySelector('.lyma-mega__content').scrollTop });
        else editorMenus.delete(sectionKey);
      }
    };
    // Keep the backdrop in the header's stacking context, below its menu panel.
    header.appendChild(overlay);
    const closeMenu = (menu, immediate = false) => {
      const state = states.get(menu);
      const panel = menu.querySelector('.lyma-mega__content');
      window.clearTimeout(state.timer);
      const version = ++state.version;
      panel.classList.remove('is-open');
      panel.classList.add('is-closing');
      menu.querySelector('summary').setAttribute('aria-expanded', 'false');
      syncOverlay();
      const finish = () => {
        if (state.version !== version) return;
        panel.classList.remove('is-mounted', 'is-closing');
        menu.open = false;
        syncOverlay();
      };
      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else state.timer = window.setTimeout(finish, 470);
    };
    const openMenu = menu => {
      if (!menu || !desktopQuery.matches) return;
      const panel = menu.querySelector('.lyma-mega__content');
      const state = states.get(menu);
      window.clearTimeout(state.timer);
      if (panel.classList.contains('is-open')) return;
      menus.forEach(other => { if (other !== menu) closeMenu(other, true); });
      const version = ++state.version;
      menu.open = true;
      syncOverlay();
      menu.querySelector('summary').setAttribute('aria-expanded', 'true');
      syncMetrics();
      panel.classList.remove('is-closing');
      panel.classList.add('is-mounted');
      void panel.offsetWidth;
      window.requestAnimationFrame(() => {
        if (state.version !== version || !desktopQuery.matches) return;
        panel.classList.add('is-open');
        syncOverlay();
      });
    };
    const closeAll = () => {
      menus.forEach(menu => { if (menu.open) closeMenu(menu); });
    };
    menus.forEach(menu => {
      const summary = menu.querySelector('summary');
      on(summary, 'click', event => {
        if (!desktopQuery.matches) return;
        event.preventDefault();
        // Avoid Dawn's separate summary handler reversing aria-expanded.
        event.stopImmediatePropagation();
        if (menu.querySelector('.is-open')) closeMenu(menu);
        else openMenu(menu);
      });
      on(menu.closest('header-menu'), 'lyma:close-menu', event => {
        if (!desktopQuery.matches) return;
        event.preventDefault();
        closeMenu(menu);
      });
      // Native details/keyboard closure must also clear the backdrop.
      on(menu, 'toggle', () => {
        if (!menu.open) {
          menu.querySelector('.lyma-mega__content').classList.remove('is-open', 'is-mounted', 'is-closing');
          syncOverlay();
        }
      });
    });
    on(nav, 'click', event => {
      if (desktopQuery.matches && event.target.closest('a')) closeAll();
    });
    on(document, 'click', event => {
      if (desktopQuery.matches && !nav.contains(event.target)) closeAll();
    });
    on(document, 'keydown', event => {
      if (!desktopQuery.matches || event.key !== 'Escape') return;
      const active = menus.find(menu => menu.open);
      if (active) { event.preventDefault(); closeAll(); active.querySelector('summary').focus(); }
    });
    on(nav, 'keyup', event => {
      if (desktopQuery.matches && event.key === 'Escape') event.stopPropagation();
    }, { capture: true });
    const scheduleMetrics = () => {
      window.cancelAnimationFrame(metricsFrame);
      metricsFrame = window.requestAnimationFrame(syncMetrics);
    };
    on(window, 'scroll', scheduleMetrics, { passive: true });
    on(window, 'resize', scheduleMetrics, { passive: true });
    const resizeObserver = new ResizeObserver(scheduleMetrics);
    resizeObserver.observe(header);
    on(desktopQuery, 'change', () => {
      menus.forEach(menu => closeMenu(menu, true));
      syncMetrics();
    });

    on(document, 'shopify:block:select', event => {
      if (header.contains(event.target)) openMenu(menus.find(menu => menu.querySelector('#HeaderMenu-shop')) || menus[0]);
    });
    this.cleanup = () => {
      if (window.Shopify?.designMode) {
        const active = menus.find(menu => menu.open);
        if (active) editorMenus.set(sectionKey, { id: active.id, scrollTop: active.querySelector('.lyma-mega__content').scrollTop });
      }
      controller.abort();
      resizeObserver.disconnect();
      window.cancelAnimationFrame(metricsFrame);
      states.forEach(state => { window.clearTimeout(state.timer); state.version++; });
      overlay.remove();
      document.documentElement.classList.remove('lyma-desktop-menu-open');
    };
    if (restore) {
      const menu = menus.find(menu => menu.id === restore.id);
      if (menu) {
        openMenu(menu);
        menu.querySelector('.lyma-mega__content').scrollTop = restore.scrollTop;
      }
    }
    }
  });
})();
